import * as bootstrap from 'bootstrap';
import * as errors from './errors';
import { ReportValidity } from './form';
import { IsCallback } from './functions';
import * as html from './html';
import { MaybePromise } from './promise';
import { UUID } from './uuid';

export interface Button {
    text: string;
    class: string;
    click: (e: MouseEvent) => void;
}

export interface Options<T = void> {
    title?: string;
    headerLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    close?: false | string;
    outsideClosable?: false;
    preSubmit?: () => MaybePromise<unknown>;
    onSubmit?: () => MaybePromise<T>;
    confirmText?: string;
    confirmHTML?: string;
    cancelText?: string;
    cancelButton?: false;
    body?: string | HTMLElement | Node;
    text?: string;
    width?: string;
    escape?: boolean;
    buttons?: Button[];
    onClose?: () => void;
    appendButtonClass?: string;
    appendConfirmButtonClass?: string;
    appendCancelButtonClass?: string;
    appendCloseButtonClass?: string;
    confirmButtonClass?: string;
    cancelButtonClass?: string;
    closeButtonClass?: string;
    animation?: boolean;
    submitAutoFocus?: boolean;
    reverseButtonOrder?: boolean;
    placement?: 'start' | 'end' | 'top' | 'bottom';
    appendClassBySelector?: Record<string, string>;
}

let defaults: Omit<Options, 'onSubmit'> | undefined;

export function SetDefaults(opts: Options) {
    defaults = opts;
}

export default class Offcanvas<T = void> {
    bootstrap: bootstrap.Offcanvas;
    $: HTMLElement;
    promise: Promise<T>;
    #resolve: (value: T) => void = () => undefined;
    #reject: (reason?: unknown) => void = () => undefined;

    #offcanvasSuccess: HTMLElement;
    #offcanvasError: HTMLElement;
    #offcanvasWarning: HTMLElement;

    #resolveClosed: () => void = () => undefined;
    closed: boolean = false;
    closedPromise: Promise<void>;

    constructor(opts?: Options<T>) {
        if (defaults) {
            if (opts) {
                opts = { ...defaults, ...opts };
            } else {
                opts = defaults as Options<T>;
            }
        }

        this.promise = new Promise<T>((resolve, reject) => {
            this.#resolve = resolve;
            this.#reject = reject;
        });

        this.closedPromise = new Promise<void>(resolve => {
            this.#resolveClosed = resolve;
        });

        const uuid = UUID();

        const div = html.FromString(`
            <div class="offcanvas offcanvas-${opts?.placement ?? 'start'}" tabindex="-1" id="newOffcanvas" aria-labelledby="newOffcanvas">
                <div class="offcanvas-header">
                     <${opts?.headerLevel ?? 'h5'} class="offcanvas-title">Offcanvas</${opts?.headerLevel ?? 'h5'}>
                    <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
                </div>
                <div class="offcanvas-body">
                    <form id="form-${uuid}">
                        <fieldset></fieldset>
                    </form>
                </div>

                <div class="offcanvas-error alert alert-danger mt-3 text-break d-none"></div>
                <div class="offcanvas-warning alert alert-warning mt-3 text-break d-none"></div>
                <div class="offcanvas-success alert alert-success mt-3 text-break d-none"></div>

                <div class="offcanvas-footer d-flex justify-content-end gap-3">
                    <button id="offcanvas-close-btn"
                        form="form-${uuid}"
                        type="button"
                        class="${opts?.cancelButtonClass ?? `btn btn-secondary ${opts?.appendButtonClass || ''} ${opts?.appendCancelButtonClass || ''}`}"
                        data-bs-dismiss="offcanvas">
                        ${html.Escape(opts?.cancelText || 'Cancel')}
                    </button>
                    <button id="offcanvas-confirm-btn"
                        type="submit"
                        form="form-${uuid}"
                        class="${opts?.confirmButtonClass ?? `btn btn-primary ${opts?.appendButtonClass || ''} ${opts?.appendConfirmButtonClass || ''}`}">
                        ${opts?.confirmHTML || html.Escape(opts?.confirmText || 'Submit')}
                    </button>
                </div>
            </div>
        `).firstElementChild as HTMLDivElement;

        if (opts?.appendClassBySelector) {
            for (const selector of Object.keys(opts?.appendClassBySelector)) {
                for (const $ of Array.from(div.querySelectorAll(selector))) {
                    $.classList.add(...opts.appendClassBySelector[selector].split(' '));
                }
            }
        }

        if (opts?.width) {
            div.style.setProperty('--bs-offcanvas-width', opts.width);
        }

        const header = div.querySelector<HTMLElement>('.offcanvas-header');
        const title = div.querySelector<HTMLElement>('.offcanvas-title');
        const closeLink = div.querySelector<HTMLElement>('.btn-close');

        const offcanvasBody = div.querySelector<HTMLElement>('.offcanvas-body');
        offcanvasBody?.addEventListener('scroll', () => {
            if (offcanvasBody.scrollTop > 0) {
                div.classList.add('offcanvas-scrolling');
            } else {
                div.classList.remove('offcanvas-scrolling');
            }
        });

        const body = div.querySelector<HTMLElement>('.offcanvas-body fieldset');
        const form = div.querySelector('.offcanvas-body form') as HTMLFormElement;
        const footer = div.querySelector<HTMLElement>('.offcanvas-footer');
        this.#offcanvasError = div.querySelector<HTMLElement>('.offcanvas-error') as HTMLElement;
        const offcanvasSuccess = div.querySelector<HTMLElement>('.offcanvas-success');
        this.#offcanvasWarning = div.querySelector<HTMLElement>('.offcanvas-warning') as HTMLElement;

        const closeButton = div.querySelector<HTMLElement>('#offcanvas-close-btn');
        const confirmButton = div.querySelector<HTMLElement>('#offcanvas-confirm-btn');

        if (opts?.reverseButtonOrder && footer && closeButton) {
            footer.append(closeButton);
        }

        if (title) {
            if (opts?.title) {
                title.textContent = opts.title;
            } else {
                title.parentElement?.removeChild(title);
            }
        }

        if (opts?.close === false) {
            closeLink?.parentElement?.removeChild(closeLink);
            closeButton?.parentElement?.removeChild(closeButton);
        } else if (opts?.close) {
            if (closeButton) {
                closeButton.textContent = opts.close;
            }
        }

        if (opts?.cancelButton === false) {
            closeButton?.parentElement?.removeChild(closeButton);
        }

        if (!opts?.title) {
            header?.parentElement?.removeChild(header);
        }

        if (opts?.outsideClosable === false) {
            div.setAttribute('data-bs-backdrop', 'static');
        }

        if (!opts?.onSubmit && !opts?.confirmText) {
            confirmButton?.parentElement?.removeChild(confirmButton);
        } else if (opts.submitAutoFocus ?? true) {
            setTimeout(() => {
                confirmButton?.focus();
                div.scrollTo(0, 0);
            });
        }

        if (opts?.text) {
            opts.body ??= html.Escape(opts.text);
        }

        if (body) {
            if (typeof opts?.body === 'string') {
                body.innerHTML = opts.body;
            } else if (opts?.body) {
                body.appendChild(opts.body);
            }
        }

        if (opts?.buttons?.length) {
            for (const btn of opts.buttons.reverse()) {
                const b = document.createElement('button');
                b.classList.add(...btn.class.split(' '));
                b.textContent = btn.text;
                b.type = 'button';
                b.addEventListener('click', e => btn.click(e));

                footer?.insertBefore(b, footer.firstChild);
            }
        }

        if (form) {
            form.noValidate = true;
            form.addEventListener('submit', async e => {
                e.preventDefault();

                body?.setAttribute('disabled', 'disabled');

                const spinner = html.FromString(`
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>`).firstElementChild as HTMLSpanElement;
                confirmButton?.insertBefore(spinner, confirmButton.firstChild);

                this.hideError();

                if (opts?.preSubmit) {
                    await opts.preSubmit();
                }
                body?.removeAttribute('disabled');
                if (!ReportValidity(form)) {
                    spinner.parentElement?.removeChild(spinner);
                    return false;
                }
                body?.setAttribute('disabled', 'disabled');

                try {
                    if (opts?.onSubmit) {
                        this.#resolve(await opts.onSubmit());
                    } else {
                        this.#resolve(undefined as unknown as T);
                    }
                    this.close();
                } catch (err: unknown) {
                    div.scrollTo(0, div.scrollHeight);

                    body?.removeAttribute('disabled');
                    spinner.parentElement?.removeChild(spinner);

                    this.#reject(err);
                    errors.ShowError(err, form, err => this.showError(err));
                }
            });
        }

        document.body.append(div);
        const bs = new bootstrap.Offcanvas(div);
        if (!offcanvasSuccess) {
            throw new Error('offcanvasSuccess is null');
        }

        this.bootstrap = bs;
        this.$ = div;
        this.#offcanvasSuccess = offcanvasSuccess;

        if (typeof opts?.escape === 'undefined' || opts?.escape) {
            const esc = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    bs.hide();
                }
            };
            document.addEventListener('keyup', esc);

            div.addEventListener('hidden.bs.offcanvas', () => document.removeEventListener('keyup', esc));
        }

        bs.show();

        div.addEventListener('hidden.bs.offcanvas', () => {
            this.close();
        });

        // hide all tooltips
        for (const e of Array.from(document.querySelectorAll('[aria-describedby*="tooltip"]'))) {
            if (IsCallback(bootstrap.Tooltip['getInstance'])) {
                bootstrap.Tooltip.getInstance(e)?.hide();
            }
        }
        if (opts?.onClose) {
            div.addEventListener('hide.bs.offcanvas', () => {
                if (opts?.onClose) {
                    opts.onClose();
                }
            });
        }
    }

    close() {
        if (this.closed) {
            return;
        }

        this.bootstrap.dispose();
        const maybeBackdrop = this.$.previousElementSibling;
        if (maybeBackdrop?.classList.contains('offcanvas-backdrop')) {
            maybeBackdrop.remove();
        }
        this.$.remove();

        // fix body overflow
        document.body.classList.remove('offcanvas-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.removeAttribute('data-bs-overflow');
        document.body.removeAttribute('data-bs-padding-right');

        this.#resolveClosed();
        this.closed = true;
    }

    showSuccess(s: string) {
        this.hideError();
        this.hideWarning();

        this.#offcanvasSuccess.textContent = s;
        this.#offcanvasSuccess.classList.remove('d-none');
    }

    hideSuccess() {
        this.#offcanvasSuccess.classList.add('d-none');
    }

    showError(err: string) {
        this.hideSuccess();
        this.hideWarning();

        this.#offcanvasError.innerHTML = html.Nl2br(html.Escape(err));
        this.#offcanvasError.classList.remove('d-none');
    }

    hideError() {
        this.#offcanvasError.classList.add('d-none');
    }

    showWarning(err: string) {
        this.hideSuccess();
        this.hideError();

        this.#offcanvasWarning.innerHTML = html.Nl2br(html.Escape(err));
        this.#offcanvasWarning.classList.remove('d-none');
    }

    hideWarning() {
        this.#offcanvasWarning.classList.add('d-none');
    }
}
